import {AfterViewInit, Directive, ElementRef, HostListener, OnDestroy} from '@angular/core';

/** Moves a modal by its header while keeping its content within the available viewport. */
@Directive({selector: '[qaDependencyDrag]'})
export class DependencyDragDirective implements AfterViewInit, OnDestroy {
  private drag: {id: number; x: number; y: number};
  private offset = {x: 0, y: 0};
  private observer: ResizeObserver;
  private content: HTMLElement;

  /** Receives the draggable header without changing any dialog controls. */
  public constructor(private element: ElementRef<HTMLElement>) {}

  /** Re-clamps moved windows when additional rules or validation change their size. */
  public ngAfterViewInit(): void {
    this.content = this.element.nativeElement.closest('.modal-content');
    if (!this.content) { return; }
    this.observer = new ResizeObserver(() => {
      if (this.offset.x || this.offset.y) { this.constrain(); }
    });
    this.observer.observe(this.content);
  }

  /** Releases pointer capture and observation when the modal closes. */
  public ngOnDestroy(): void { this.end(); this.observer?.disconnect(); }

  /** Starts dragging only on the header's noninteractive area. */
  @HostListener('pointerdown', ['$event'])
  public start(event: PointerEvent): void {
    if (event.button !== 0 || !event.isPrimary || (event.target as Element).closest('button, input, select')) { return; }
    this.element.nativeElement.setPointerCapture(event.pointerId);
    this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY};
    event.preventDefault();
  }

  /** Applies pointer movement to the content rather than the centered dialog wrapper. */
  @HostListener('document:pointermove', ['$event'])
  public move(event: PointerEvent): void {
    if (!this.drag || this.drag.id !== event.pointerId) { return; }
    this.constrain(event.clientX - this.drag.x, event.clientY - this.drag.y);
    this.drag.x = event.clientX;
    this.drag.y = event.clientY;
  }

  /** Stops dragging on either completion or cancellation. */
  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  public end(): void {
    if (this.drag && this.element.nativeElement.hasPointerCapture(this.drag.id)) {
      this.element.nativeElement.releasePointerCapture(this.drag.id);
    }
    this.drag = null;
  }

  /** Keeps the whole window reachable after movement or viewport changes. */
  @HostListener('window:resize')
  public constrain(dx = 0, dy = 0): void {
    if (!this.content) { return; }
    const bounds = this.content.getBoundingClientRect();
    this.offset.x += Math.max(8 - bounds.left, Math.min(dx, window.innerWidth - 8 - bounds.right));
    this.offset.y += Math.max(8 - bounds.top, Math.min(dy, window.innerHeight - 8 - bounds.bottom));
    this.content.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px)`;
  }
}
